import { emitCanvasFocusNodes } from "@/pages/canvas/constants/canvasFocusNodes.constants";
import { cn } from "@/utils";
import { ChevronDown, Shapes } from "lucide-react";
import { AnimatePresence, motion } from "motion/react";
import { useCallback, useEffect, useId, useRef, useState } from "react";
import type { CanvasAddSummary } from "./canvasAddSummary";

/** Sentinel key for "the whole set", as opposed to a single group's tool call id. */
const ALL_KEY = "all";

/** How long a failed-navigation note stays on screen before clearing itself. */
const UNREACHABLE_NOTE_MS = 3000;

const UNREACHABLE_NOTE = "Can’t navigate there";

/** The row's own click already zooms, so this is the affordance, not a control. */
const LOCATE_LABEL = "Locate";

/** Rust link affordance, sized like the design system's `Disclose`. */
const LOCATE_LINK =
  "shrink-0 font-sans text-xs font-medium text-rust underline underline-offset-2 transition-opacity duration-150 ease-out";

function nodeCount(n: number): string {
  return `${n} node${n === 1 ? "" : "s"}`;
}

/**
 * The collapsed line's label. Three-way, because a turn can create nodes,
 * update them, or both. The adds-only string is the issue's original
 * acceptance criterion and is deliberately byte-identical to what shipped.
 * In the both case the "node/nodes" word rides the added count alone, so it
 * reads "3 nodes added, 2 updated".
 */
function summaryLabel(added: number, updated: number): string {
  if (updated === 0) return `${nodeCount(added)} added to canvas`;
  if (added === 0) return `${nodeCount(updated)} updated on canvas`;
  return `${nodeCount(added)} added, ${updated} updated`;
}

/** Same three cases, spoken for the summary row's zoom button. */
function summaryZoomLabel(added: number, updated: number): string {
  const what =
    updated === 0
      ? `${nodeCount(added)} added`
      : added === 0
        ? `${nodeCount(updated)} updated`
        : `${nodeCount(added)} added and ${updated} updated`;
  return `Zoom the canvas to the ${what} in this response`;
}

interface CanvasNodesAddedSummaryProps {
  summary: CanvasAddSummary;
}

/**
 * One collapsed line at the foot of an assistant turn summarizing the canvas
 * nodes that turn touched ("5 nodes added to canvas", "2 nodes updated on
 * canvas"), expandable into one row per touching call. Clicking the line fits
 * the canvas viewport to every touched node; clicking a row fits it to just
 * that call's nodes.
 *
 * The chat also renders where no canvas is mounted (workspace/session chat),
 * and nodes can be deleted after the edit. Both degrade the same way: the
 * focus event is cancelable and only a canvas that actually navigated acks it,
 * so an unacked click says so instead of silently doing nothing.
 */
export function CanvasNodesAddedSummary({
  summary,
}: CanvasNodesAddedSummaryProps) {
  const [open, setOpen] = useState(false);
  const [unreachableKey, setUnreachableKey] = useState<string | null>(null);
  const unreachableTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const listId = useId();

  useEffect(
    () => () => {
      if (unreachableTimer.current) clearTimeout(unreachableTimer.current);
    },
    [],
  );

  const handleFocus = useCallback((key: string, nodeIds: string[]) => {
    if (unreachableTimer.current) clearTimeout(unreachableTimer.current);
    if (emitCanvasFocusNodes(nodeIds)) {
      setUnreachableKey(null);
      return;
    }
    setUnreachableKey(key);
    unreachableTimer.current = setTimeout(
      () => setUnreachableKey(null),
      UNREACHABLE_NOTE_MS,
    );
  }, []);

  return (
    <div>
      <div className="mb-1 flex cursor-pointer items-center gap-2 px-4 py-2">
        <button
          type="button"
          onClick={() => handleFocus(ALL_KEY, summary.nodeIds)}
          aria-label={summaryZoomLabel(
            summary.totalAdded,
            summary.totalUpdated,
          )}
          className="group flex min-w-0 flex-1 cursor-pointer items-center gap-2 text-left outline-none"
        >
          {/* 24px glyph gutter with bg-background backing, matching every rail row. */}
          <span className="relative z-[1] inline-flex size-6 shrink-0 items-center justify-center bg-background text-rust">
            <Shapes className="size-[18px]" />
          </span>
          <span className="min-w-0 flex-1 truncate font-sans text-sm text-n-60 transition-colors duration-150 ease-out group-hover:text-n-80">
            {summaryLabel(summary.totalAdded, summary.totalUpdated)}
          </span>
          <span className={cn(LOCATE_LINK, "group-hover:opacity-80")}>
            {LOCATE_LABEL}
          </span>
        </button>

        {unreachableKey === ALL_KEY && (
          <span role="status" className="shrink-0 font-sans text-xs text-n-50">
            {UNREACHABLE_NOTE}
          </span>
        )}

        {summary.groups.length >= 1 && (
          <button
            type="button"
            onClick={() => setOpen((o) => !o)}
            aria-expanded={open}
            aria-controls={listId}
            aria-label={
              open ? "Hide canvas edit breakdown" : "Show canvas edit breakdown"
            }
            className="group/toggle -mr-1 shrink-0 cursor-pointer rounded p-1 outline-none"
          >
            <ChevronDown
              className={cn(
                "size-3.5 text-n-50 transition-all duration-150 ease-out group-hover/toggle:text-n-70",
                open && "rotate-180",
              )}
            />
          </button>
        )}
      </div>

      {/* Same height/opacity collapse the turn rail uses (200ms easeInOut). */}
      <AnimatePresence initial={false}>
        {open && (
          <motion.div
            key="canvas-add-groups"
            id={listId}
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.2, ease: "easeInOut" }}
            className="overflow-hidden"
          >
            {summary.groups.map((group) => {
              const rowLabel = group.description ?? group.fallbackLabel;
              return (
                <button
                  key={group.toolCallId}
                  type="button"
                  onClick={() => handleFocus(group.toolCallId, group.nodeIds)}
                  aria-label={`Zoom the canvas to ${nodeCount(
                    group.nodeIds.length,
                  )} from ${rowLabel}`}
                  className="group/row flex w-full cursor-pointer items-center gap-2 py-1.5 pl-12 pr-4 text-left outline-none"
                >
                  <span className="min-w-0 flex-1 truncate font-sans text-sm text-n-70 group-hover/row:text-n-90">
                    {rowLabel}
                  </span>
                  {unreachableKey === group.toolCallId && (
                    <span
                      role="status"
                      className="shrink-0 font-sans text-xs text-n-50"
                    >
                      {UNREACHABLE_NOTE}
                    </span>
                  )}
                  {/* The count lives on in the aria-label — only the visible
                      grey count is replaced by this affordance. */}
                  <span
                    className={cn(LOCATE_LINK, "group-hover/row:opacity-80")}
                  >
                    {LOCATE_LABEL}
                  </span>
                </button>
              );
            })}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
